import { useCallback, useMemo, useState } from 'react'

import { BrandsItem } from '@/screens/Home/page/types/types'

/** Проверяем, является ли символ кириллическим (А-Я, Ё). */
function isCyrillicLetter(char: string): boolean {
	return /[а-яё]/i.test(char)
}

/** Группируем BrandsItem в словарь: { [letter]: [...brands] } */
function groupBrands(categories: BrandsItem[]) {
	const groupedAll: Record<string, BrandsItem[]> = {}
	for (const brand of categories) {
		const letter = brand.NAME.trim().charAt(0).toUpperCase()
		if (!groupedAll[letter]) {
			groupedAll[letter] = []
		}
		groupedAll[letter].push(brand)
	}
	return groupedAll
}

/**
 * Кастомный хук, который:
 * - Группирует бренды
 * - Вычисляет массив букв (ALL_LETTERS)
 * - Фильтрует верхние буквы (topLetters)
 * - Следит за activeLetter и скроллом
 *
 * Убрали функционал с ?sort=...
 */
export function useBrandsAlphabet(categories: BrandsItem[]) {
	// 1) Группируем все бренды
	const groupedAll = useMemo(() => groupBrands(categories), [categories])

	// 2) Список ВСЕХ букв (латиница, кириллица и пр.), отсортированный
	const ALL_LETTERS = useMemo(() => {
		return Object.keys(groupedAll).sort()
	}, [groupedAll])

	// 3) Верхняя строка: не выводим кириллические буквы
	const topLetters = useMemo(() => {
		return ALL_LETTERS.filter(letter => !isCyrillicLetter(letter))
	}, [ALL_LETTERS])

	// 4) Проверяем, есть ли хотя бы одна кириллическая буква
	const hasCyrillic = useMemo(() => {
		return ALL_LETTERS.some(letter => isCyrillicLetter(letter))
	}, [ALL_LETTERS])

	// 5) Активная буква (для подсветки и скролла)
	const [activeLetter, setActiveLetter] = useState<string | null>(null)

	// 6) Скролл к нужному блоку (мемоизирован)
	const scrollToLetter = useCallback((letter: string) => {
		const element = document.getElementById(`letter-${letter}`)
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}
	}, [])

	// 7) Клик по латинской (или любой) букве
	const onClickLetter = useCallback(
		(letter: string) => {
			setActiveLetter(letter)
			scrollToLetter(letter)
			// Больше ничего не делаем (не меняем URL)
		},
		[scrollToLetter]
	)

	// 8) Клик по «А-Я»: ищем первую кириллическую букву
	const onClickRussian = useCallback(() => {
		const firstCyrillic = ALL_LETTERS.find(letter => isCyrillicLetter(letter))
		if (firstCyrillic) {
			setActiveLetter(firstCyrillic)
			scrollToLetter(firstCyrillic)
		}
	}, [ALL_LETTERS, scrollToLetter])

	// 9) Подсвечивать кнопку «А-Я», если активная буква — кириллическая
	const isRussianActive = useMemo(() => {
		return activeLetter ? isCyrillicLetter(activeLetter) : false
	}, [activeLetter])

	return {
		groupedAll,
		ALL_LETTERS,
		topLetters,
		hasCyrillic,
		activeLetter,
		isRussianActive,
		onClickLetter,
		onClickRussian
	}
}
